import React, { useState } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

// Step 5 — the first goal. Templated asks (filled with what they sell) become
// a real engine task immediately, optionally repeated as a weekday-morning
// routine in the business timezone.
const StepFirstGoal = ({ status, wizardState, showNotification, onDone, onBack }) => {
  const agent = wizardState.agentEmail
    ? { name: wizardState.agentName, email: wizardState.agentEmail }
    : status.agents?.[0]
      ? { name: status.agents[0].name, email: status.agents[0].email }
      : null;

  // wizardState is React-only — on resume it's empty, so fall back to the
  // server copy (businessDetails.whatYouSell, returned by GET /status).
  // Without this a resumed customer got a product-less generic first goal.
  const product = (wizardState.whatYouSell || status?.whatYouSell || '').split(/[.\n]/)[0].trim();
  const templates = [
    `Find 20 companies that look like good prospects for us${product ? ` (${product.toLowerCase()})` : ''}, with contact names and emails, and send me a shortlist.`,
    'Research our top 3 competitors — pricing, positioning, weaknesses — and email me a summary.',
    `Draft an intro email pitching what we sell${product ? ` (${product.toLowerCase()})` : ''} and send it to me for review before anything goes out.`,
  ];

  const [goal, setGoal] = useState('');
  const [repeatDaily, setRepeatDaily] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!agent || goal.trim().length < 10 || sending) return;
    setSending(true);
    try {
      const res = await axiosInstance.post('/api/business/onboarding/first-goal', {
        agentEmail: agent.email,
        summary: goal.trim(),
        repeatDaily,
      });
      if (res.data?.data?.routine?.error) {
        showNotification?.(res.data.data.routine.error, 'info');
      }
      onDone({});
    } catch (error) {
      logger.error('First goal failed:', error);
      showNotification?.(error.response?.data?.message || 'Could not assign the goal', 'error');
    } finally {
      setSending(false);
    }
  };

  if (!agent) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Hire an agent first — then give it its first goal.</p>
        {onBack && (
          <button onClick={onBack} className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium">Back</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Give {agent.name} a first goal</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        This becomes a real task right now — {agent.name} will report back to you by email with receipts.
      </p>

      <div className="space-y-2 mb-4">
        {templates.map((t, i) => (
          <button
            key={i}
            onClick={() => setGoal(t)}
            className={`w-full text-left text-sm px-3 py-2.5 rounded-lg border transition-colors ${
              goal === t
                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10 text-gray-900 dark:text-white'
                : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <textarea
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[80px]"
        value={goal}
        onChange={(e) => setGoal(e.target.value)}
        placeholder="…or write your own goal in plain language"
      />

      <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={repeatDaily}
          onChange={(e) => setRepeatDaily(e.target.checked)}
          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
        />
        Repeat this every weekday at 9:00 AM ({status.timezone || 'your timezone'})
      </label>

      <div className="flex justify-between mt-6">
        {onBack ? <button onClick={onBack} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button> : <span />}
        <button
          onClick={submit}
          disabled={goal.trim().length < 10 || sending}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium"
        >
          {sending ? 'Assigning…' : `Put ${agent.name} to work`}
        </button>
      </div>
    </div>
  );
};

export default StepFirstGoal;
